import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Calendar, Package } from 'lucide-react';
import { useEmail } from '../contexts/EmailContext';
import { callWFAPI } from '../utils/helper';
export function PendingRentals({
  refreshKey,
  shouldExpand,
  onExpandChange
}) {
  const {
    email
  } = useEmail();
  const [rentals, setRentals] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (shouldExpand) {
      setIsExpanded(true);
      if (onExpandChange) {
        onExpandChange(false);
      }
    }
  }, [shouldExpand, onExpandChange]);
  useEffect(() => {
    if (email) {
      loadRentals();
    }
  }, [refreshKey, email]);
  const loadRentals = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        criteria: '(Email == "' + email + '")'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      if (resp.length > 0) {
        const loadedRentals = resp.map((item: any) => {
          const shippingAddress = item.Shipping_Address || {};
          return {
            id: item.ID,
            date: item.added,
            customerInfo: {
              email: item.Email,
              eventName: item.Event_Name,
              eventVenueCityState: item.Event_Venue_City_State || '',
              name: item.Name,
              notes: item.Notes,
              phone: item.Phone_Number,
              address: shippingAddress.address_line_1 || '',
              address2: shippingAddress.address_line_2 || '',
              city: shippingAddress.district_city || '',
              state: shippingAddress.state_province || '',
              zip: shippingAddress.postal_code || '',
              proposedEventDate: item.Proposed_Event_Date || '',
              alternateDate: item.Alternate_Date || '',
              preferredTechnician: item.Technician?.ID || ''
            },
            total: parseFloat(item.Total_Amount),
            subtotal: parseFloat(item.Subtotal),
            discount: parseFloat(item.Discount_Amount || 0),
            tax: parseFloat(item.California_Tax),
            selections: {
              gen3Body: item.Gen3_Body_Quantity,
              gen3Head: item.Gen3_Head_Quantity,
              gen3JudgesOnly: item.Gen3_Judges_Only_Quantity,
              fullGen3Ring: item.Full_Gen3_Ring_Quantity,
              requireTech: item.Requires_Technician && item.Requires_Technician === 'true',
              numberOfDays: parseInt(item.Number_Of_Days) || 1
            }
          };
        });
        setRentals(loadedRentals);
      } else {
        setRentals([]);
      }
    } catch (error) {
      console.error('Error loading rentals:', error);
      setRentals([]);
    } finally {
      setLoading(false);
    }
  };
  // Only show if user has an email (meaning they've submitted a request)
  if (!email || rentals.length === 0) {
    return null;
  }
  return <div className="bg-white rounded-lg shadow-md mb-8 overflow-hidden" data-id="element-1259">
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1260">
        <div className="flex items-center gap-3" data-id="element-1261">
          <Package size={20} className="text-blue-600" data-id="element-1262" />
          <h3 className="text-lg font-semibold text-slate-800" data-id="element-1263">
            Your Rental Requests
          </h3>
          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded" data-id="element-1264">
            {rentals.length}
          </span>
        </div>
        {isExpanded ? <ChevronUp size={20} className="text-slate-600" data-id="element-1265" /> : <ChevronDown size={20} className="text-slate-600" data-id="element-1266" />}
      </button>

      {isExpanded && <div className="border-t border-slate-200" data-id="element-1267">
          <div className="p-6 space-y-4" data-id="element-1268">
            {loading ? <div className="text-center py-8 text-slate-600" data-id="element-1269">
                Loading rentals...
              </div> : rentals.map(rental => <div key={rental.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-1270">
                  <div className="flex justify-between items-start mb-3" data-id="element-1271">
                    <div data-id="element-1272">
                      <h4 className="font-semibold text-slate-800" data-id="element-1273">
                        {rental.customerInfo.eventName || 'Event'}
                      </h4>
                      <p className="text-sm text-slate-600" data-id="element-1274">
                        {rental.customerInfo.name}
                      </p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded border border-yellow-200" data-id="element-1275">
                      Pending
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-1276">
                    <div className="flex items-center text-sm text-slate-600" data-id="element-1277">
                      <Calendar size={14} className="mr-2 text-slate-400" data-id="element-1278" />
                      <span data-id="element-1279">
                        {rental.customerInfo.proposedEventDate ? (() => {
                  const startDate = new Date(rental.customerInfo.proposedEventDate + 'T00:00:00');
                  const startDateStr = startDate.toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  if (rental.selections.numberOfDays > 1) {
                    const endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                    const endDateStr = endDate.toLocaleDateString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    return `${startDateStr} - ${endDateStr} (${rental.selections.numberOfDays} Days)`;
                  }
                  return startDateStr;
                })() : 'Date TBD'}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-1280">
                      <span className="font-medium" data-id="element-1281">Total:</span> $
                      {rental.total.toFixed(2)}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs" data-id="element-1282">
                    {rental.selections.fullGen3Ring > 0 && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded" data-id="element-1283">
                        {rental.selections.fullGen3Ring} Full Ring
                        {rental.selections.fullGen3Ring !== 1 ? 's' : ''}
                      </span>}
                    {rental.selections.gen3Body > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded border border-slate-200" data-id="element-1284">
                        {rental.selections.gen3Body} Body
                      </span>}
                    {rental.selections.gen3Head > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded border border-slate-200" data-id="element-1285">
                        {rental.selections.gen3Head} Head
                      </span>}
                    {rental.selections.gen3JudgesOnly > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded border border-slate-200" data-id="element-1286">
                        {rental.selections.gen3JudgesOnly} Judges
                      </span>}
                    {rental.selections.requireTech && <span className="bg-red-50 text-red-700 px-2 py-1 rounded border border-red-200" data-id="element-1287">
                        Technician
                      </span>}
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-id="element-1288">
                    Submitted: {new Date(rental.date).toLocaleString()}
                  </div>
                </div>)}
          </div>
        </div>}
    </div>;
}